// API Types
export interface ApiResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
  all?: number[]
}

// Notification Types
export interface Toast {
  id?: string
  content: string
  delay: number
  delayRemaining?: number
  action?: () => void
  actionName?: string
  classname?: string
  error?: any
  /** When set, popup is shown only while this org is active in the session. */
  organizationId?: number | null
}

export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_superuser: boolean
  is_staff: boolean
  is_active: boolean
  date_joined: string
  groups: number[]
  /** Roles in the current organization (id + display name); read-only from API. */
  groups_detail?: Array<{ id: number; name: string }>
  organizations?: number[]
  default_organization_id?: number | null
  organization_roles?: Record<string, number[]>
  user_permissions: string[]
  inherited_permissions: string[]
  password?: string
  is_mfa_enabled?: boolean
  /** Authentication source: 'local' for password users, 'ldap' for AD-provisioned users. */
  auth_source?: 'local' | 'ldap'
}

export interface Group {
  id: number
  name: string
  permissions: string[]
  user_set: number[]
}

export interface Organization {
  id: number
  name: string
  is_active: boolean
  users_count?: number
  is_default?: boolean
}

export interface SupportedUploadType {
  mime_type: string
  extensions: string[]
}

export interface UiSettings {
  user: User
  settings: Record<string, any>
  permissions: string[]
  organizations?: Array<{ id: number; name: string; is_default?: boolean }>
  currentOrganizationId?: number | null
  version?: string
  update_checking?: {
    backend_setting: string
  }
  supported_upload_types?: SupportedUploadType[]
  allowed_upload_mime_types?: string[] | null
}

export interface PackageFeatureFlags {
  packageId: string | null
  packageName: string | null
  features: Record<string, unknown>
  updatedAt: string | null
}

export const DEFAULT_PACKAGE_FEATURE_FLAGS: PackageFeatureFlags = {
  packageId: null,
  packageName: null,
  features: {},
  updatedAt: null,
}

export interface CustomFieldInstance {
  id?: number
  field: number
  /** Read-only custom field metadata for display-only views without `view_customfield`. */
  field_display?: Pick<CustomField, 'id' | 'name' | 'data_type' | 'placeholder' | 'extra_data'>
  value: any
  required?: boolean
  counter_info?: {
    assigned: boolean
    sequence_number?: number | null
    sequence_key?: string | null
    missing_variables?: string[]
  } | null
  document: number
  created?: Date | string
}

export interface CustomField {
  id: number
  name: string
  data_type: string
  placeholder?: string | null
  default_value?: any
  created?: Date | string
  extra_data?: {
    select_options?: Array<{ label: string; id: string }>
    default_currency?: string
    counter?: {
      template?: string
      padding?: number
      start_value?: number
      variables?: Array<{ name: string; custom_field_id: number }>
    }
  }
  counter_type_configs?: Array<{
    document_type_id: number
    document_type_name: string
    counter_config: CounterConfig | null
  }>
  document_count?: number
  parent_id?: number | null
  owner?: number | null
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

export interface Tag {
  id: number
  name: string
  color: string
  text_color: string
  is_inbox_tag: boolean
  parent: number | null
  matching_algorithm: number
  match: string
  is_insensitive: boolean
  document_count?: number
  owner?: number | null
  children?: Tag[]
  depth?: number
  orderIndex?: number
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

// Correspondent types
export interface Correspondent {
  id: number
  name: string
  slug: string
  matching_algorithm: number
  match: string
  is_insensitive: boolean
  document_count: number
  last_correspondence: string | null
  owner?: number | null
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

// Storage Path types
export interface StoragePath {
  id: number
  name: string
  slug: string
  path: string
  document_count: number
  matching_algorithm?: number
  match?: string
  is_insensitive?: boolean
  owner?: number | null
  set_permissions?: {
    view: { users: number[]; groups: number[] }
    change: { users: number[]; groups: number[] }
  }
  permissions?: {
    view: { users: number[]; groups: number[] }
    change: { users: number[]; groups: number[] }
  }
}

export interface SavedView {
  id: number
  name: string
  show_on_dashboard: boolean
  show_in_sidebar: boolean
  sort_field: string
  sort_reverse: boolean
  filter_rules: FilterRule[]
  page_size?: number | null
  owner: number | null
  document_count?: number
}

export interface FilterRule {
  rule_type: number | string // API uses number, but React implementation uses string filter vars
  value: string | number | boolean
}

export enum DocumentSource {
  ConsumeFolder = 1,
  ApiUpload = 2,
  MailFetch = 3,
  WebUI = 4,
  SefFetch = 5,
  EOtpremnicaFetch = 6,
}

export enum WorkflowTriggerType {
  Consumption = 1,
  DocumentAdded = 2,
  DocumentUpdated = 3,
  Scheduled = 4,
  DocumentWorkflowStatusChanged = 5,
}

export enum ScheduleDateField {
  Added = 'added',
  Created = 'created',
  Modified = 'modified',
  CustomField = 'custom_field',
}

export enum TriggerFilterType {
  TagsAny = 'tags_any',
  TagsAll = 'tags_all',
  TagsNone = 'tags_none',
  CorrespondentIs = 'correspondent_is',
  CorrespondentNot = 'correspondent_not',
  DocumentTypeIs = 'document_type_is',
  DocumentTypeNot = 'document_type_not',
  StoragePathIs = 'storage_path_is',
  StoragePathNot = 'storage_path_not',
  CustomFieldQuery = 'custom_field_query',
}

export interface WorkflowTrigger {
  id?: number
  type: WorkflowTriggerType
  sources?: DocumentSource[]
  filter_filename?: string
  filter_path?: string
  filter_mailrule?: number
  match?: string
  matching_algorithm?: number
  is_insensitive?: boolean
  filter_has_tags?: number[]
  filter_has_all_tags?: number[]
  filter_has_not_tags?: number[]
  filter_has_not_correspondents?: number[]
  filter_has_not_document_types?: number[]
  filter_has_not_storage_paths?: number[]
  filter_custom_field_query?: string | null
  filter_has_correspondent?: number | null
  filter_has_document_type?: number | null
  filter_has_storage_path?: number | null
  filter_workflow_status?: number | null
  filter_has_not_workflow_statuses?: number[]
  schedule_offset_days?: number
  schedule_is_recurring?: boolean
  schedule_recurring_interval_days?: number
  schedule_date_field?: ScheduleDateField
  schedule_date_custom_field?: number
  filters?: WorkflowTriggerFilter[]
}

export interface WorkflowTriggerFilter {
  type: TriggerFilterType
  values: any
  queryMode?: string
  fieldId?: number
  operator?: string
  value?: any
  showJson?: boolean
  jsonQuery?: string
  hasMultipleRules?: boolean
  rules?: QueryRule[]
  expressions?: QueryExpression[]
  customFieldQueryModel?: any
}

export interface QueryRule {
  fieldId?: number | null
  operator?: string
  value?: any
}

export interface QueryExpression {
  operator?: string
  elements?: QueryElement[]
  depth?: number
}

export interface QueryElement {
  type: 'atom' | 'expression'
  atom?: QueryAtom
  expression?: QueryExpression
}

export interface QueryAtom {
  fieldId?: number | null
  operator?: string
  value?: any
}

export enum WorkflowActionType {
  Assignment = 1,
  Removal = 2,
  Email = 3,
  Webhook = 4,
  AttributeAndActionPermissions = 6,
}

export interface WorkflowAttributePermissionRule {
  field_id?: number
  system_field?: string
  visible: boolean
  editable: boolean
  required: boolean
}

export interface WorkflowDocumentActionPermissionRule {
  target_type: 'user' | 'group'
  target_ids: number[]
  actions: string[]
}

export interface WorkflowActionEmail {
  id?: number
  subject?: string
  body?: string
  to?: string
  include_document?: boolean
}

export interface WorkflowActionWebhook {
  id?: number
  url?: string
  use_params?: boolean
  as_json?: boolean
  params?: Record<string, string>
  body?: string
  headers?: Record<string, string>
  include_document?: boolean
}

export interface WorkflowAction {
  id?: number
  type: WorkflowActionType
  assign_title?: string
  assign_tags?: number[]
  assign_document_type?: number
  assign_correspondent?: number
  assign_storage_path?: number
  assign_workflow_status?: number | null
  assign_owner?: number
  assign_view_users?: number[]
  assign_view_groups?: number[]
  assign_change_users?: number[]
  assign_change_groups?: number[]
  assign_custom_fields?: number[]
  assign_custom_fields_required?: number[]
  assign_custom_fields_values?: Record<number, any>
  remove_tags?: number[]
  remove_all_tags?: boolean
  remove_document_types?: number[]
  remove_all_document_types?: boolean
  remove_correspondents?: number[]
  remove_all_correspondents?: boolean
  remove_storage_paths?: number[]
  remove_all_storage_paths?: boolean
  remove_workflow_status?: boolean
  remove_owners?: number[]
  remove_all_owners?: boolean
  remove_view_users?: number[]
  remove_view_groups?: number[]
  remove_change_users?: number[]
  remove_change_groups?: number[]
  permission_document_type?: number | null
  permission_users?: number[]
  permission_groups?: number[]
  permission_attribute_rules?: WorkflowAttributePermissionRule[]
  permission_action_rules?: WorkflowDocumentActionPermissionRule[]
  remove_all_permissions?: boolean
  remove_custom_fields?: number[]
  remove_all_custom_fields?: boolean
  email?: WorkflowActionEmail
  webhook?: WorkflowActionWebhook
}

export interface Workflow {
  id?: number
  name: string
  order: number
  enabled: boolean
  triggers: WorkflowTrigger[]
  actions: WorkflowAction[]
}

export interface DocDotTask {
  id: number
  task_id: string
  task_file_name: string
  task_name: string
  date_created: string
  date_done: string | null
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE'
  result: string | null
  acknowledged: boolean
  related_document?: number
}

export interface SystemStatus {
  database_status: 'OK' | 'ERROR'
  database_error?: string
  storage_path_status: 'OK' | 'ERROR'
  storage_path_error?: string
  index_status: 'OK' | 'ERROR'
  index_error?: string
  classifier_status: 'OK' | 'ERROR'
  classifier_error?: string
}

// Mail Account Types
export enum IMAPSecurity {
  None = 1,
  SSL = 2,
  STARTTLS = 3,
}

export enum MailAccountType {
  IMAP = 1,
  Gmail_OAuth = 2,
  Outlook_OAuth = 3,
}

export interface MailAccount {
  id: number
  name: string
  imap_server: string
  imap_port: number
  imap_security: IMAPSecurity
  username: string
  password: string
  character_set?: string
  is_token: boolean
  account_type: MailAccountType
  expiration?: string
  use_for_transactional_emails?: boolean
  smtp_server?: string
  smtp_port?: number
  smtp_security?: IMAPSecurity
  owner: number | null
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

// Mail Rule Types
export enum MailFilterAttachmentType {
  Attachments = 1,
  Everything = 2,
}

export enum MailRuleConsumptionScope {
  Attachments = 1,
  EmailOnly = 2,
  Everything = 3,
}

export enum MailRulePdfLayout {
  Default = 0,
  TextHtml = 1,
  HtmlText = 2,
  HtmlOnly = 3,
  TextOnly = 4,
}

export enum MailAction {
  Delete = 1,
  Move = 2,
  MarkRead = 3,
  Flag = 4,
  Tag = 5,
}

export enum MailMetadataTitleOption {
  FromSubject = 1,
  FromFilename = 2,
  None = 3,
}

export enum MailMetadataCorrespondentOption {
  FromNothing = 1,
  FromEmail = 2,
  FromName = 3,
  FromCustom = 4,
}

export interface MailRule {
  id: number
  name: string | null
  account: number | null // MailAccount.id
  order: number | null
  enabled: boolean
  folder: string
  filter_from: string | null
  filter_to: string | null
  filter_subject: string | null
  filter_body: string | null
  filter_attachment_filename_include: string | null
  filter_attachment_filename_exclude: string | null
  maximum_age: number | null
  consumption_scope: MailRuleConsumptionScope
  attachment_type: MailFilterAttachmentType
  pdf_layout: MailRulePdfLayout
  action: MailAction
  action_parameter?: string | null
  assign_title_from: MailMetadataTitleOption
  assign_tags?: number[] // Tag.id
  assign_document_type?: number | null // DocumentType.id
  assign_correspondent_from?: MailMetadataCorrespondentOption
  assign_correspondent?: number | null // Correspondent.id
  assign_owner_from_rule: boolean
  owner: number | null
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

// Saved View Types
export enum DisplayMode {
  TABLE = 'table',
  SMALL_CARDS = 'smallCards',
  LARGE_CARDS = 'largeCards',
  HIERARCHY = 'hierarchy',
}

export interface DisplayField {
  id: string
  name: string
}

export interface SavedView {
  id: number
  name: string
  show_on_dashboard: boolean
  show_in_sidebar: boolean
  sort_field: string
  sort_reverse: boolean
  filter_rules: FilterRule[]
  page_size?: number | null
  display_mode?: DisplayMode
  display_fields?: DisplayField[] | string[]
  owner: number | null
  document_count?: number
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  user_can_change?: boolean
}

// Document types
export interface Document {
  id: number
  title: string
  content: string
  created: string
  added: string
  modified: string
  archive_serial_number: number | null
  correspondent: Correspondent | null
  /** Read-only display payload for permission-limited users. */
  correspondent_display?: Pick<Correspondent, 'id' | 'name'> | null
  document_type: DocumentType | null
  /** Read-only display payload for permission-limited users. */
  document_type_display?: Pick<DocumentType, 'id' | 'name'> | null
  workflow_status: WorkflowStatus | null
  claimed_by?: number | User | null
  claimed_at?: string | null
  storage_path: StoragePath | null
  /** Read-only display payload for permission-limited users. */
  storage_path_display?: Pick<StoragePath, 'id' | 'name'> | null
  tags: number[]
  /** Read-only: minimal tag payload for display when `view_tag` is missing. */
  tags_display?: Array<Pick<Tag, 'id' | 'name' | 'color' | 'text_color' | 'parent'>>
  /** Owner id for writes; list/detail may return a number from API */
  owner?: number | User | null
  /** Read-only: basic account fields for display when caller lacks `view_user` */
  owner_display?: Pick<User, 'id' | 'username' | 'first_name' | 'last_name'> | null
  /** Read-only names for permission chips when user/group lists are unavailable. */
  permissions_display?: {
    view: {
      users: Array<Pick<User, 'id' | 'username' | 'first_name' | 'last_name'>>
      groups: Array<Pick<Group, 'id' | 'name'>>
    }
    change: {
      users: Array<Pick<User, 'id' | 'username' | 'first_name' | 'last_name'>>
      groups: Array<Pick<Group, 'id' | 'name'>>
    }
  }
  original_file_name: string
  archived_file_name: string
  mime_type: string
  page_count: number
  notes: DocumentNote[]
  custom_fields: CustomFieldInstance[]
  workflow_permissions?: {
    actions: Record<string, boolean>
    attributes: Record<string, { visible: boolean; editable: boolean; required: boolean }>
    system_attributes: Record<string, { visible: boolean; editable: boolean; required: boolean }>
  }
  newly_assigned_counter_values?: string[]
  is_shared_by_requester: boolean
  version_number: number
  is_current?: boolean
  version_comment?: string | null
  version_created_by?: User | null
  version_group_title?: string | null
  version_group_id?: number | null
  search_score?: number
}

export type DocumentRelationSource = 'manual' | 'sef_auto'

export interface DocumentRelatedItem {
  document_id: number
  title: string
  created: string | null
  relation_sources: DocumentRelationSource[]
  editable: boolean
}

export interface DocumentVersion {
  id: number
  version_number: number
  is_current: boolean
  added: string
  created: string
  version_comment?: string | null
  version_created_by?: User | null
  has_archive_version: boolean
  mime_type: string
  original_file_name?: string | null
  archived_file_name?: string | null
}

export type DocumentNoteUser = Pick<User, 'id' | 'username' | 'first_name' | 'last_name'>

export interface DocumentNote {
  id: number
  note: string
  created: string
  user: DocumentNoteUser | number
}

// Duplicate interface removed - see CustomField definition above

// Correspondent types
export interface Correspondent {
  id: number
  name: string
  slug: string
  matching_algorithm: number
  match: string
  is_insensitive: boolean
  document_count: number
  last_correspondence: string | null
  owner?: number | null
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

export interface TemplateMappingItem {
  custom_field_id: number
  pattern: string
}

export interface TemplateMapping {
  template_document_id?: number
  mappings: TemplateMappingItem[]
}

export interface DocumentTemplate {
  id: number
  name: string
  original_filename: string
  tags: string[]
  mappings: Record<string, number>
  stale_mappings: string[]
  file_size?: number | null
  created_at: string
  updated_at: string
}

// Workflow Status types (e.g. Draft, Sent, In Review, Approved, Rejected)
export interface WorkflowStatus {
  id: number
  name: string
  slug: string
  color: string
  text_color: string
  matching_algorithm: number
  match: string
  is_insensitive: boolean
  document_count: number
  owner?: number | null
  permissions?: {
    view: { users: number[]; groups: number[] }
    change: { users: number[]; groups: number[] }
  }
  set_permissions?: {
    view: { users: number[]; groups: number[] }
    change: { users: number[]; groups: number[] }
  }
}

/** Document workflow task: one per (document, step, assignee). Read-only from API. */
export interface DocumentWorkflowTask {
  id: number
  document_id: number
  document_title: string
  workflow_definition_id: number
  workflow_definition_name: string
  step_id: number
  step_name: string
  assigned_to: number
  assigned_to_username: string
  status: 'open' | 'resolved' | 'cancelled'
  created_at: string
  resolved_at: string | null
  resolved_by: number | null
}

// Document Type types
export interface DocumentType {
  id: number
  name: string
  slug: string
  matching_algorithm: number
  match: string
  is_insensitive: boolean
  document_count: number
  owner?: number | null
  custom_fields?: number[] // IDs of custom fields associated with this document type
  custom_field_configs?: DocumentTypeCustomFieldConfig[]
  workflow_definitions?: number[] // IDs of document workflow definitions allowed for this type
  template_mapping?: TemplateMapping
  template_active?: boolean
  word_templates?: DocumentTemplate[]
  permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
  set_permissions?: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

export interface CounterConfig {
  template: string
  padding?: number
  start_value?: number
  variables?: Array<{ name: string; custom_field_id: number | null }>
}

export interface DocumentTypeCustomFieldConfig {
  custom_field: number
  order: number
  required: boolean
  override_name?: string | null
  override_placeholder?: string | null
  override_default_value?: any
  visibility_query?: any
  counter_config?: CounterConfig | null
  custom_field_name?: string
  custom_field_data_type?: string
  custom_field_placeholder?: string | null
  custom_field_default_value?: any
}

// Storage Path types
export interface StoragePath {
  id: number
  name: string
  slug: string
  path: string
  document_count: number
  matching_algorithm?: number
  match?: string
  is_insensitive?: boolean
  owner?: number | null
  set_permissions?: {
    view: { users: number[]; groups: number[] }
    change: { users: number[]; groups: number[] }
  }
  permissions?: {
    view: { users: number[]; groups: number[] }
    change: { users: number[]; groups: number[] }
  }
}
